import { supabase } from './supabase';
import { logger } from './logger';

/**
 * Admin Impersonation Service
 * Allows super admin (ryan@meetwithreps.com) to impersonate other users for customer support
 */

const SUPER_ADMIN_EMAIL = 'ryan@meetwithreps.com';

export const adminImpersonationService = {
  /**
   * Check if current user is super admin
   */
  isSuperAdmin: (user) => {
    if (!user) return false;
    return user.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  },

  /**
   * Get all users for account switcher (both providers and sales reps)
   */
  getAllUsers: async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, user_type, company, specialty')
        .order('first_name', { ascending: true });

      if (error) {
        logger.error('Error fetching users for impersonation', { error: error.message });
        throw error;
      }

      // Format users for display
      const users = (data || []).map(user => ({
        id: user.id,
        email: user.email,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
        userType: user.user_type,
        company: user.company,
        specialty: user.specialty,
        displayLabel: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
        secondaryLabel: user.user_type === 'customer'
          ? (user.specialty || user.company || 'Healthcare Provider')
          : user.user_type === 'sales'
          ? (user.company || 'Sales Representative')
          : user.user_type === 'office_staff'
          ? (user.company || 'Office Staff')
          : 'User'
      }));

      logger.debug('Fetched users for impersonation', { count: users.length });
      return users;
    } catch (error) {
      logger.error('Failed to fetch users for impersonation', { error: error.message });
      throw error;
    }
  },

  /**
   * Start impersonation session
   * @param {string} targetUserId - User ID to impersonate
   * @param {object} adminUser - Current admin user
   */
  startImpersonation: async (targetUserId, adminUser) => {
    try {
      // Verify admin is super admin
      if (!adminImpersonationService.isSuperAdmin(adminUser)) {
        throw new Error('Unauthorized: Only super admin can impersonate users');
      }

      // Get target user details
      const { data: targetUser, error: userError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', targetUserId)
        .single();

      if (userError || !targetUser) {
        logger.error('Error fetching target user for impersonation', { error: userError?.message });
        throw new Error('User not found');
      }

      // If target is office_staff, fetch their delegation relationship
      if (targetUser.user_type === 'office_staff') {
        try {
          const { data: adminRelationship, error: adminError } = await supabase
            .from('admin_users')
            .select('customer_id, role, status')
            .eq('staff_id', targetUser.id)
            .eq('status', 'active')
            .maybeSingle();

          if (!adminError && adminRelationship) {
            // Fetch the physician profile they're delegating for
            const { data: physicianProfile, error: physicianError } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', adminRelationship.customer_id)
              .single();

            if (!physicianError && physicianProfile) {
              targetUser.delegating_for = adminRelationship.customer_id;
              targetUser.delegatingFor = {
                id: physicianProfile.id,
                name: `${physicianProfile.first_name} ${physicianProfile.last_name}`.trim(),
                fullName: `${physicianProfile.first_name} ${physicianProfile.last_name}`.trim(),
                email: physicianProfile.email,
                phone: physicianProfile.phone,
                company: physicianProfile.company,
                location: physicianProfile.location,
                specialty: physicianProfile.specialty,
                npi_number: physicianProfile.npi_number,
                user_type: physicianProfile.user_type,
                ...physicianProfile
              };
              targetUser.adminRole = adminRelationship.role;
              logger.debug('Impersonated office_staff managing physician:', targetUser.delegatingFor.name);
            }
          }
        } catch (delegationError) {
          logger.warn('Error fetching delegation for impersonated office_staff:', delegationError);
        }
      }

      // Log impersonation start
      const { error: logError } = await supabase
        .from('admin_impersonation_log')
        .insert({
          admin_user_id: adminUser.id,
          admin_email: adminUser.email,
          target_user_id: targetUserId,
          target_email: targetUser.email,
          action: 'start_impersonation',
          timestamp: new Date().toISOString()
        });

      if (logError) {
        logger.warn('Failed to log impersonation', { error: logError.message });
        // Don't throw - logging failure shouldn't block impersonation
      }

      // Store impersonation state in sessionStorage
      sessionStorage.setItem('admin_impersonation', JSON.stringify({
        adminUserId: adminUser.id,
        adminEmail: adminUser.email,
        adminName: `${adminUser.first_name || ''} ${adminUser.last_name || ''}`.trim(),
        targetUserId: targetUser.id,
        targetEmail: targetUser.email,
        startedAt: new Date().toISOString()
      }));

      logger.info('Started impersonation session', {
        admin: adminUser.email,
        target: targetUser.email
      });

      return targetUser;
    } catch (error) {
      logger.error('Failed to start impersonation', { error: error.message });
      throw error;
    }
  },

  /**
   * End impersonation session and return to admin account
   * @param {object} adminUser - Admin user info from impersonation state
   */
  endImpersonation: async (adminUser) => {
    try {
      const impersonationState = adminImpersonationService.getImpersonationState();

      if (impersonationState) {
        // Log impersonation end
        const { error: logError } = await supabase
          .from('admin_impersonation_log')
          .insert({
            admin_user_id: impersonationState.adminUserId,
            admin_email: impersonationState.adminEmail,
            target_user_id: impersonationState.targetUserId,
            target_email: impersonationState.targetEmail,
            action: 'end_impersonation',
            timestamp: new Date().toISOString()
          });

        if (logError) {
          logger.warn('Failed to log impersonation end', { error: logError.message });
        }
      }

      // Clear impersonation state
      sessionStorage.removeItem('admin_impersonation');

      // Get admin user profile
      const { data: adminUserProfile, error: adminError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', SUPER_ADMIN_EMAIL)
        .single();

      if (adminError || !adminUserProfile) {
        logger.error('Error fetching admin user', { error: adminError?.message });
        throw new Error('Admin user not found');
      }

      logger.info('Ended impersonation session', { admin: SUPER_ADMIN_EMAIL });
      return adminUserProfile;
    } catch (error) {
      logger.error('Failed to end impersonation', { error: error.message });
      throw error;
    }
  },

  /**
   * Get current impersonation state from sessionStorage
   */
  getImpersonationState: () => {
    try {
      const stateStr = sessionStorage.getItem('admin_impersonation');
      return stateStr ? JSON.parse(stateStr) : null;
    } catch (error) {
      logger.warn('Failed to parse impersonation state', { error: error.message });
      return null;
    }
  },

  /**
   * Check if currently impersonating
   */
  isImpersonating: () => {
    return adminImpersonationService.getImpersonationState() !== null;
  }
};

export default adminImpersonationService;
